import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { itemAPI } from '../../api/items';
import { Item } from '../../types/entities';
import { Plus, Edit, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

export const ItemList: React.FC = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      const data = await itemAPI.getAll();
      setItems(data);
    } catch (error) {
      toast.error(t('items.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('items.deleteConfirm'))) return;

    try {
      await itemAPI.delete(id);
      toast.success(t('items.deleteSuccess'));
      loadItems();
    } catch (error) {
      toast.error(t('items.deleteError'));
    }
  };

  const getUnitLabel = (unit: string) => {
    return t(`items.units.${unit}`, { defaultValue: unit });
  };

  if (loading) return <div className="text-center py-10">{t('common.loading')}</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">{t('items.title')}</h1>
        <button
          onClick={() => navigate('/items/new')}
          className="btn-primary flex items-center"
        >
          <Plus className="w-5 h-5 mr-2" />
          {t('items.addNew')}
        </button>
      </div>

      <div className="card">
        <table className="w-full">
          <thead className="border-b">
            <tr className="text-left">
              <th className="pb-3 table-header">{t('items.name')}</th>
              <th className="pb-3 table-header">{t('items.defaultUnit')}</th>
              <th className="pb-3 table-header">{t('items.description')}</th>
              <th className="pb-3 table-header">{t('common.status')}</th>
              <th className="pb-3 table-header">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-8 text-center text-gray-500">
                  {t('common.noData')}
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="border-b last:border-0">
                  <td className="py-3 font-medium">{item.name}</td>
                  <td className="py-3">{getUnitLabel(item.defaultUnit)}</td>
                  <td className="py-3">{item.description || '-'}</td>
                  <td className="py-3">
                    <span
                      className={`px-2 py-1 rounded text-sm ${
                        item.status === 'ACTIVE'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {item.status === 'ACTIVE' ? t('common.active') : t('common.inactive')}
                    </span>
                  </td>
                  <td className="py-3">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => navigate(`/items/${item.id}/edit`)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        title={t('common.edit')}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                        title={t('common.delete')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
